'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { AnyWhitelabelConfigKey } from '@/types/enums/WhitelabelConfig';
import type { WhitelabelConfigResponse } from '@/types/models/WhitelabelConfig';
import { resolveWhitelabelValue } from '@/utils/whitelabelConfig';

type WhitelabelConfigContextType = {
  config: WhitelabelConfigResponse | null;
  getValue: <T = string>(key: AnyWhitelabelConfigKey) => T | undefined;
};

const WhitelabelConfigContext = createContext<WhitelabelConfigContextType | null>(null);

type WhitelabelConfigProviderProps = {
  initialConfig: WhitelabelConfigResponse | null;
  children: ReactNode;
};

export const WhitelabelConfigProvider = ({
  initialConfig,
  children,
}: WhitelabelConfigProviderProps) => {
  const value = useMemo<WhitelabelConfigContextType>(
    () => ({
      config: initialConfig,
      getValue: (key) => resolveWhitelabelValue(initialConfig, key),
    }),
    [initialConfig],
  );

  return (
    <WhitelabelConfigContext.Provider value={value}>
      {children}
    </WhitelabelConfigContext.Provider>
  );
};

export const useWhitelabelConfigContext = () => {
  const ctx = useContext(WhitelabelConfigContext);
  if (!ctx) {
    throw new Error('useWhitelabelConfig must be used within a WhitelabelConfigProvider');
  }
  return ctx;
};
